import { useNavigate } from 'react-router-dom';
import { Briefcase, FileSearch, FileSignature, LucideIcon } from 'lucide-react';
import { Sheet } from './Sheet';

interface CreateItem {
  label: string;
  desc: string;
  icon: LucideIcon;
  to: string;
  state?: Record<string, unknown>;
  track: string;
}

/** The only verb surface in the app — everything else in nav is a noun/place. */
const ITEMS: CreateItem[] = [
  {
    label: 'Ask a legal question',
    desc: 'A cited research memo — English, Hindi or Malayalam.',
    icon: FileSearch,
    to: '/app/research/new',
    track: 'create-menu-research',
  },
  {
    label: 'Draft a document',
    desc: 'Notices, applications, petitions — 17 document types.',
    icon: FileSignature,
    to: '/app/drafts/new',
    track: 'create-menu-draft',
  },
  {
    label: 'New case',
    desc: 'Hearings, research, drafts and documents in one place.',
    icon: Briefcase,
    to: '/app/cases',
    state: { create: true },
    track: 'create-menu-case',
  },
  // "Upload a document" was removed (founder, July 2026): both chat intakes
  // attach documents natively, so a standalone upload entry is a parameter
  // masquerading as a function (redundant-card rule). Deliberate uploads
  // live on the All-documents library and inside case files.
];

/**
 * The ＋ New menu, shared by the desktop sidebar (popover) and the mobile
 * bottom nav (bottom sheet). Same items, same order, same wording (§2.1/2.2).
 */
export function CreateMenu({
  variant,
  onClose,
}: {
  variant: 'popover' | 'sheet';
  onClose: () => void;
}) {
  const navigate = useNavigate();

  const items = ITEMS.map((item) => (
    <button
      key={item.track}
      type="button"
      data-track={item.track}
      onClick={() => {
        onClose();
        navigate(item.to, item.state ? { state: item.state } : undefined);
      }}
      className="w-full flex items-start gap-3 px-3 py-3 rounded-lg hover:bg-legal-50 text-left transition-colors press"
    >
      <span className="w-9 h-9 rounded-lg bg-accent-50 border border-accent/25 flex items-center justify-center shrink-0">
        <item.icon className="h-4 w-4 text-accent-700" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-legal-900">{item.label}</span>
        <span className="block text-xs text-legal-400 mt-0.5">{item.desc}</span>
      </span>
    </button>
  ));

  if (variant === 'sheet') {
    return (
      <Sheet title="Create" onClose={onClose}>
        {items}
      </Sheet>
    );
  }

  return (
    <>
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className="fixed inset-0 z-40 cursor-default"
      />
      <div className="absolute left-0 right-0 top-full mt-2 z-50 bg-white border border-legal-200 rounded-card shadow-elevation-3 p-1.5 animate-slide-up">
        {items}
      </div>
    </>
  );
}
